import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { estModuleOptionnel, PALIERS, type Palier } from '@artosera/shared';
import { Badge, Button, Input, Select, type BadgeTone } from '@artosera/ui';
import { listerGaleries } from '../lib/api.js';
import { usePeut } from '../auth/usePeut.js';
import { useLibellesModules } from '../lib/modules.js';
import { ETATS_GALERIE, type EtatGalerie, type GalerieListe } from '../types.js';
import { BadgeTest } from './EspaceDeTest.js';
import pageStyles from '../styles/page.module.css';
import { TableauDefilant } from '../components/TableauDefilant.js';
import tableStyles from '../styles/table.module.css';

const TONE_ETAT: Record<EtatGalerie, BadgeTone> = {
  active: 'available',
  suspendue: 'reserved',
  resiliee: 'sold',
  purge_en_cours: 'sold',
  purgee: 'sold',
};

/** Filtre des espaces de test (lot B) : tous, sans eux, ou eux seuls. */
const FILTRES_TEST = ['tous', 'masquer', 'seulement'] as const;
type FiltreTest = (typeof FILTRES_TEST)[number];

function formaterDate(valeur: string | null, jamais: string): string {
  if (!valeur) return jamais;
  return new Date(valeur).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Les modules complémentaires EFFECTIFS d'une galerie (D21) : le cœur est à
 * tout le monde, le répéter dans chaque ligne noierait l'essentiel.
 */
function ModulesEffectifs({ modules }: { modules: readonly string[] }): JSX.Element {
  const { t } = useTranslation('console');
  const libelles = useLibellesModules();
  const complementaires = modules.filter(estModuleOptionnel);
  if (complementaires.length === 0) {
    return <span className={tableStyles.celluleMuette}>{t('galeries.coeurSeul')}</span>;
  }
  return <>{libelles.liste(complementaires)}</>;
}

export function GalleriesList(): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const navigate = useNavigate();
  const requete = useQuery({ queryKey: ['console', 'galeries'], queryFn: listerGaleries });

  const [recherche, setRecherche] = useState('');
  const [filtreEtat, setFiltreEtat] = useState<EtatGalerie | 'tous'>('tous');
  const [filtrePalier, setFiltrePalier] = useState<Palier | 'tous'>('tous');
  const [filtreTest, setFiltreTest] = useState<FiltreTest>('tous');

  const filtrees = useMemo(() => {
    const galeries = requete.data ?? [];
    const terme = recherche.trim().toLowerCase();
    return galeries.filter((galerie: GalerieListe) => {
      if (filtreEtat !== 'tous' && galerie.etat !== filtreEtat) return false;
      if (filtrePalier !== 'tous' && galerie.palier !== filtrePalier) return false;
      if (filtreTest === 'masquer' && galerie.estTest) return false;
      if (filtreTest === 'seulement' && !galerie.estTest) return false;
      if (terme && !galerie.nom.toLowerCase().includes(terme) && !galerie.slug.includes(terme)) {
        return false;
      }
      return true;
    });
  }, [requete.data, recherche, filtreEtat, filtrePalier, filtreTest]);

  const optionsEtat = [
    { value: 'tous', label: t('galeries.tousEtats') },
    ...ETATS_GALERIE.map((valeur) => ({ value: valeur, label: t(`galeries.etat.${valeur}`) })),
  ];
  const optionsPalier = [
    { value: 'tous', label: t('galeries.tousPaliers') },
    ...PALIERS.map((valeur) => ({ value: valeur, label: t(`galeries.palier.${valeur}`) })),
  ];
  const optionsTest = FILTRES_TEST.map((valeur) => ({
    value: valeur,
    label: t(`espaceTest.filtre.${valeur}`),
  }));

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('galeries.titre')}</h1>
          <p>{t('galeries.texte')}</p>
        </div>
        {peut('console:manage-onboarding') ? (
          <Button onClick={() => navigate('/ouvertures/nouvelle')}>
            {t('galeries.nouvelleGalerie')}
          </Button>
        ) : null}
      </div>

      <div className={tableStyles.toolbar}>
        <div className={tableStyles.champRecherche}>
          <label htmlFor="galeries-recherche">{t('galeries.rechercheLabel')}</label>
          <Input
            id="galeries-recherche"
            placeholder={t('galeries.recherchePlaceholder')}
            value={recherche}
            onChange={(event) => setRecherche(event.target.value)}
          />
        </div>
        <div className={tableStyles.champFiltre}>
          <label htmlFor="galeries-filtre-etat">{t('galeries.filtreEtatLabel')}</label>
          <Select
            id="galeries-filtre-etat"
            options={optionsEtat}
            value={filtreEtat}
            onValueChange={(valeur) => setFiltreEtat(valeur as EtatGalerie | 'tous')}
          />
        </div>
        <div className={tableStyles.champFiltre}>
          <label htmlFor="galeries-filtre-palier">{t('galeries.filtrePalierLabel')}</label>
          <Select
            id="galeries-filtre-palier"
            options={optionsPalier}
            value={filtrePalier}
            onValueChange={(valeur) => setFiltrePalier(valeur as Palier | 'tous')}
          />
        </div>
        <div className={tableStyles.champFiltre}>
          <label htmlFor="galeries-filtre-test">{t('espaceTest.filtreLabel')}</label>
          <Select
            id="galeries-filtre-test"
            options={optionsTest}
            value={filtreTest}
            onValueChange={(valeur) => setFiltreTest(valeur as FiltreTest)}
          />
        </div>
      </div>

      {filtrees.length === 0 ? (
        <p className={pageStyles.videEtat}>{t('galeries.vide')}</p>
      ) : (
        <TableauDefilant>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th scope="col">{t('galeries.colonnes.nom')}</th>
                <th scope="col">{t('galeries.colonnes.palier')}</th>
                <th scope="col">{t('galeries.colonnes.etat')}</th>
                <th scope="col">{t('galeries.colonnes.modules')}</th>
                <th scope="col">{t('galeries.colonnes.domaines')}</th>
                <th scope="col">{t('galeries.colonnes.membres')}</th>
                <th scope="col">{t('galeries.colonnes.dernierePublication')}</th>
                <th scope="col">{t('galeries.colonnes.derniereSauvegarde')}</th>
              </tr>
            </thead>
            <tbody>
              {filtrees.map((galerie: GalerieListe) => (
                <tr key={galerie.id}>
                  <td>
                    <Link to={`/galeries/${galerie.id}`} className={tableStyles.cellulePrincipale}>
                      {galerie.nom}
                    </Link>
                    {galerie.estTest ? (
                      <>
                        {' '}
                        <BadgeTest />
                      </>
                    ) : null}
                    <div className={tableStyles.celluleMuette}>{galerie.slug}</div>
                  </td>
                  <td>{t(`galeries.palier.${galerie.palier}`)}</td>
                  <td>
                    <Badge tone={TONE_ETAT[galerie.etat]}>
                      {t(`galeries.etat.${galerie.etat}`)}
                    </Badge>
                  </td>
                  <td>
                    <ModulesEffectifs modules={galerie.modules} />
                  </td>
                  <td>{galerie.domaines}</td>
                  <td>{galerie.membres}</td>
                  <td>{formaterDate(galerie.dernierePublication, t('galeries.jamais'))}</td>
                  <td>{formaterDate(galerie.derniereSauvegarde, t('galeries.jamais'))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableauDefilant>
      )}
    </section>
  );
}
